/**
 * Goals and exit rules: when one is met for a record, its run ends there,
 * as "goal reached" (counted in the goal conversion) or "exited".
 *
 * Each is an event ("the deal is won", "they click 'Book a survey'") or
 * conditions on the record ("owner is set"). Events end the run as they
 * happen; conditions are checked before every step and whenever the record
 * changes. A workflow that sends email always exits when they unsubscribe or
 * bounce; that rule is shown here but is not one of the list.
 */
import { ActionIcon, Badge, Button, Card, Group, SegmentedControl, Select, Stack, Text, TextInput, ThemeIcon, Tooltip } from '@mantine/core';
import { IconFlag, IconPlus, IconTrash } from '@tabler/icons-react';
import type { RecordType, WorkflowGoal, WorkflowListResponse, WorkflowTrigger } from '../../api/workflows';
import { TOKENS } from '../../theme';
import { useVocab } from '../../vocab';
import { ConditionsEditor } from './ConditionsCard';
import { EventPicker } from './TriggerCard';
import { eventWords } from './workflowModel';

/** At most this many (`MAX_GOALS`). */
export const MAX_GOALS = 10;

export function blankGoal(existing: WorkflowGoal[]): WorkflowGoal {
  const taken = new Set(existing.map((g) => g.id));
  let n = existing.length + 1;
  while (taken.has(`goal-${n}`)) n += 1;
  return { id: `goal-${n}`, outcome: 'goal_reached', event: { kind: 'deal_won' }, conditions: [], label: '' };
}

/** A goal in words, as the server says it when it has no label. */
export function goalWords(v: ReturnType<typeof useVocab>, g: WorkflowGoal): string {
  if (g.label?.trim()) return g.label.trim();
  if (g.event) {
    const w = eventWords(v, g.event);
    return w[0]!.toUpperCase() + w.slice(1);
  }
  return 'The conditions became true';
}

export function GoalsCard({
  goals, onChange, rt, list, sendsEmail, canManage,
}: {
  goals: WorkflowGoal[];
  onChange: (next: WorkflowGoal[]) => void;
  rt: RecordType;
  list: WorkflowListResponse | undefined;
  sendsEmail: boolean;
  canManage: boolean;
}) {
  const v = useVocab();
  const set = (i: number, patch: Partial<WorkflowGoal>) =>
    onChange(goals.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  return (
    <Card padding="sm" withBorder data-testid="goals-card">
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon variant="light" radius="xl" size={28} color="teal"><IconFlag size={15} /></ThemeIcon>
          <div>
            <Text size="sm" fw={600}>Goals and exits</Text>
            <Text size="xs" c="dimmed">
              {`When one is met, the ${v.one('run')} for that record ends there: goal reached, or exited.`}
            </Text>
          </div>
        </Group>
        {goals.map((g, i) => (
          <Stack
            key={g.id || i}
            gap="xs"
            p="xs"
            style={{ border: `1px solid ${TOKENS.border}`, borderRadius: 8 }}
            data-testid="goal"
          >
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Group gap="xs" wrap="wrap">
                <SegmentedControl
                  size="xs"
                  data={[{ value: 'goal_reached', label: 'Goal reached' }, { value: 'exited', label: 'Exit' }]}
                  value={g.outcome}
                  onChange={(o) => set(i, { outcome: o as WorkflowGoal['outcome'] })}
                  data-testid="goal-outcome"
                />
                <Select
                  size="xs"
                  aria-label="When"
                  data={[{ value: 'event', label: 'When something happens' }, { value: 'conditions', label: 'When something is true' }]}
                  value={g.event ? 'event' : 'conditions'}
                  allowDeselect={false}
                  onChange={(m) => set(i, m === 'event'
                    ? { event: { kind: 'deal_won' }, conditions: [] }
                    : { event: null, conditions: [] })}
                  w={210}
                  data-testid="goal-mode"
                />
              </Group>
              {canManage && (
                <Tooltip label="Remove" openDelay={400}>
                  <ActionIcon variant="subtle" color="red" aria-label={`Remove goal ${i + 1}`} onClick={() => onChange(goals.filter((_, j) => j !== i))}>
                    <IconTrash size={15} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
            {g.event ? (
              <EventPicker event={g.event as WorkflowTrigger} onChange={(event) => set(i, { event })} label="Event" testId="goal-event" />
            ) : (
              <ConditionsEditor rt={rt} conditions={g.conditions} onChange={(conditions) => set(i, { conditions })} list={list} />
            )}
            <TextInput
              size="xs"
              label="Called"
              placeholder={goalWords(v, { ...g, label: '' })}
              value={g.label ?? ''}
              onChange={(e) => set(i, { label: e.currentTarget.value })}
              maxLength={120}
            />
          </Stack>
        ))}
        {sendsEmail && (
          <Group gap="xs" wrap="nowrap" data-testid="email-exit">
            <Badge variant="light" color="gray" radius="sm" tt="none">Always</Badge>
            <Text size="xs" c="dimmed">{`Exit when they unsubscribe or their email bounces, because this ${v.one('workflow')} sends email.`}</Text>
          </Group>
        )}
        {canManage && goals.length < MAX_GOALS && (
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => onChange([...goals, blankGoal(goals)])}
            style={{ alignSelf: 'flex-start' }}
            data-testid="add-goal"
          >
            Add a goal or exit
          </Button>
        )}
      </Stack>
    </Card>
  );
}
